import React, { useMemo } from 'react';

import CarouselSitterCard from '@rover/components/src/cards/CarouselSitterCard';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';
import SnappingCarousel from '@rover/react-lib/src/components/lists/Carousel/SnappingCarousel';
import { OnPaginateAction } from '@rover/react-lib/src/components/lists/Carousel/utils/onPaginateEvent.types';
import { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import { PetType } from '@rover/types/src/Pet';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { getIsPriceTransparency } from '@rover/utilities/priceTransparency';

import { HeadingVariant } from '../../../components/lists/Carousel/components/Header';
import PaginationWrapper from '../../../components/lists/Carousel/PaginationWrapper';
import { SeoLocationPageProps } from '../SeoLocation/types';

import PriceTransparencySubheading from './PriceTransparencySubheading';

type Props = {
  searchResults: OptimizedSearchResult[];
  language: string;
  onSearchResultCardClick?: () => void;
  preferCatReviews: boolean;
  displayExpanded?: boolean;
  heading: string;
  headingVariant: HeadingVariant;
  headingSize?: ThemeHeadingSizes;
  onPaginate?: OnPaginateAction;
  onSearchResultCardViewed?: React.ComponentProps<typeof CarouselSitterCard>['onViewIntersect'];
  rootCarouselRef?: React.RefObject<HTMLElement>;
  serviceType: ServiceType;
  priceTransparencyLevel: PriceTransparencyLevel;
  countryConfig: SeoLocationPageProps['countryConfig'];
  petType?: PetType;
};

const SearchResultsCarousel: React.FC<Props> = ({
  searchResults,
  heading,
  headingVariant = 'display',
  headingSize,
  serviceType,
  priceTransparencyLevel,
  onPaginate,
  onSearchResultCardClick,
  onSearchResultCardViewed,
  countryConfig,
  petType,
  ...props
}: Props) => {
  const subheading = getIsPriceTransparency(priceTransparencyLevel) ? (
    <PriceTransparencySubheading
      priceTransparencyLevel={priceTransparencyLevel}
      serviceType={serviceType}
      countryConfig={countryConfig}
      petType={petType}
    />
  ) : null;

  const memoizedSearchResults = useMemo(
    () =>
      searchResults.map((searchResult) => (
        <CarouselSitterCard
          priceTransparencyLevel={priceTransparencyLevel}
          key={searchResult.personOpk}
          searchResult={searchResult}
          onClick={onSearchResultCardClick}
          onViewIntersect={onSearchResultCardViewed}
          itemProp="provider"
          {...props}
        />
      )),
    [onSearchResultCardClick, onSearchResultCardViewed, props, searchResults]
  );
  return (
    <PaginationWrapper
      heading={heading}
      subheading={subheading}
      onPaginate={onPaginate}
      headingVariant={headingVariant}
      headingSize={headingSize}
    >
      <SnappingCarousel>{memoizedSearchResults}</SnappingCarousel>
    </PaginationWrapper>
  );
};
export default SearchResultsCarousel;
